import React, { useState } from 'react';
import { X, FileText, AlertCircle, CheckCircle, Plus, Code, Briefcase, GraduationCap, Award, DollarSign, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { libraries } from '../../services/apiService';

const ParsedDataPanel = ({
  isOpen,
  onClose,
  aiRawData,
  unmapped,
  onToolCreated,
  allowCreateTool = true,
  onRemap
}) => {
  const [showRawJson, setShowRawJson] = useState(false);
  const [creatingTools, setCreatingTools] = useState(new Set());
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const [isRemapping, setIsRemapping] = useState(false);

  if (!isOpen || !aiRawData) return null;

  // Unmapped counts per section
  const unmappedToolsCount = unmapped?.tools?.length || 0;
  const unmappedProfessionsCount = unmapped?.professions?.length || 0;
  const unmappedLanguagesCount = unmapped?.languages?.length || 0;
  const unmappedContactsCount = unmapped?.contacts?.length || 0;
  const hasUnmappedLocation = !!(unmapped?.country || unmapped?.city);

  const totalUnmapped = unmappedToolsCount + unmappedProfessionsCount +
    unmappedLanguagesCount + unmappedContactsCount +
    (unmapped?.country ? 1 : 0) + (unmapped?.city ? 1 : 0);

  // Split mapped vs unmapped for display
  const unmappedToolsSet = new Set(unmapped?.tools?.map(t => t.toLowerCase()) || []);
  const mappedTools = (aiRawData.tools || []).filter(t => !unmappedToolsSet.has(t.toLowerCase()));

  const unmappedProfSet = new Set(unmapped?.professions?.map(p => p.toLowerCase()) || []);
  const mappedProfessions = (aiRawData.professions || []).filter(p => !unmappedProfSet.has(p.toLowerCase()));

  const unmappedLangSet = new Set(unmapped?.languages?.map(l => l.language?.toLowerCase()) || []);
  const mappedLanguages = (aiRawData.languages || []).filter(l => !unmappedLangSet.has(l.language?.toLowerCase()));

  const unmappedContactKeys = new Set(
    unmapped?.contacts?.map(c => `${c.type?.toLowerCase()}-${c.value?.toLowerCase()}`) || []
  );
  const mappedContacts = (aiRawData.contacts || []).filter(
    c => !unmappedContactKeys.has(`${c.type?.toLowerCase()}-${c.value?.toLowerCase()}`)
  );

  // Section header helper
  const SectionHeader = ({ title, totalCount, unmappedCount, alwaysGreen = false }) => {
    const hasUnmapped = !alwaysGreen && unmappedCount > 0;
    const mappedCount = totalCount - unmappedCount;

    return (
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary flex items-center gap-2">
          {hasUnmapped ? (
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          ) : (
            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
          )}
          {title}{totalCount > 0 ? ` (${totalCount})` : ''}
        </h3>
        {hasUnmapped && totalCount > 0 && (
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary ml-6 mt-0.5">
            {mappedCount} mapped, {unmappedCount} unmapped
          </p>
        )}
      </div>
    );
  };

  const handleCreateTool = async (toolName) => {
    if (!allowCreateTool) return;

    setCreatingTools(prev => new Set(prev).add(toolName));

    try {
      const response = await libraries.createTool({ name: toolName });
      const newTool = response.data;

      toast.success(`Tool "${toolName}" created successfully`);

      if (onToolCreated) {
        onToolCreated(newTool.id, toolName);
      }
    } catch (error) {
      console.error('Error creating tool:', error);
      toast.error(`Failed to create tool "${toolName}": ${error.message}`);
    } finally {
      setCreatingTools(prev => {
        const next = new Set(prev);
        next.delete(toolName);
        return next;
      });
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 z-50 flex h-[100dvh] w-full min-w-0 flex-col overflow-hidden border-l border-light-border-primary bg-light-bg-primary shadow-xl transition-transform dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:w-[650px] lg:w-[700px]">
        {/* Header */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-light-border-primary bg-light-bg-primary p-4 dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:p-5">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
              AI Parsed Data
            </h2>
            {totalUnmapped > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                {totalUnmapped} unmapped
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-secondary dark:hover:text-dark-text-secondary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-5">
          {/* Toolbar: Re-map + Toggle Raw JSON */}
          <div className="-mt-2 mb-2 flex flex-wrap items-center justify-between gap-2">
            <div>
              {onRemap && totalUnmapped > 0 && (
                <button
                  onClick={async () => {
                    setIsRemapping(true);
                    try {
                      await onRemap();
                    } finally {
                      setIsRemapping(false);
                    }
                  }}
                  disabled={isRemapping}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-lg transition-colors disabled:cursor-not-allowed shadow-sm"
                >
                  <RefreshCw className={`w-4 h-4 ${isRemapping ? 'animate-spin' : ''}`} />
                  {isRemapping ? 'Re-mapping...' : 'Re-map'}
                </button>
              )}
            </div>
            <button
              onClick={() => setShowRawJson(!showRawJson)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-light-border-primary dark:border-dark-border-primary rounded-lg hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
            >
              <Code className="w-4 h-4" />
              {showRawJson ? 'Show Formatted' : 'Show Raw JSON'}
            </button>
          </div>

          {showRawJson ? (
            /* Raw JSON View */
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 overflow-x-auto border border-light-border-primary dark:border-dark-border-primary">
              <pre className="text-sm text-gray-800 dark:text-green-400 font-mono whitespace-pre-wrap break-words">
                {JSON.stringify(aiRawData, null, 2)}
              </pre>
            </div>
          ) : (
            /* Formatted View */
            <>
              {/* Basic Information */}
              <div className={`rounded-lg p-4 border ${hasUnmappedLocation
                ? 'bg-amber-50/50 dark:bg-amber-950/10 border-amber-300 dark:border-amber-700/50'
                : 'bg-light-bg-secondary dark:bg-dark-bg-primary border-light-border-primary dark:border-dark-border-primary'
              }`}>
                <SectionHeader
                  title="Basic Information"
                  totalCount={0}
                  unmappedCount={hasUnmappedLocation ? 1 : 0}
                  alwaysGreen={!hasUnmappedLocation}
                />
                {hasUnmappedLocation && (
                  <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary ml-6 -mt-2 mb-3">
                    {[unmapped?.country && 'country', unmapped?.city && 'city'].filter(Boolean).join(', ')} not found in database
                  </p>
                )}
                <div className="space-y-2 text-sm">
                  {aiRawData.first_name && (
                    <div>
                      <span className="text-light-text-tertiary dark:text-dark-text-tertiary">First Name: </span>
                      <span className="text-light-text-primary dark:text-dark-text-primary font-medium">{aiRawData.first_name}</span>
                    </div>
                  )}
                  {aiRawData.last_name && (
                    <div>
                      <span className="text-light-text-tertiary dark:text-dark-text-tertiary">Last Name: </span>
                      <span className="text-light-text-primary dark:text-dark-text-primary font-medium">{aiRawData.last_name}</span>
                    </div>
                  )}
                  {aiRawData.name && (
                    <div>
                      <span className="text-light-text-tertiary dark:text-dark-text-tertiary">Name: </span>
                      <span className="text-light-text-primary dark:text-dark-text-primary font-medium">{aiRawData.name}</span>
                    </div>
                  )}
                  {aiRawData.country && (
                    <div className="flex items-center gap-2">
                      <span className="text-light-text-tertiary dark:text-dark-text-tertiary">Country: </span>
                      <span className="text-light-text-primary dark:text-dark-text-primary font-medium">{aiRawData.country}</span>
                      {unmapped?.country && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-200">unmapped</span>
                      )}
                    </div>
                  )}
                  {aiRawData.city && (
                    <div className="flex items-center gap-2">
                      <span className="text-light-text-tertiary dark:text-dark-text-tertiary">City: </span>
                      <span className="text-light-text-primary dark:text-dark-text-primary font-medium">{aiRawData.city}</span>
                      {unmapped?.city && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-200">unmapped</span>
                      )}
                    </div>
                  )}
                  {aiRawData.birthday && (
                    <div>
                      <span className="text-light-text-tertiary dark:text-dark-text-tertiary">Birthday: </span>
                      <span className="text-light-text-primary dark:text-dark-text-primary font-medium">{aiRawData.birthday}</span>
                    </div>
                  )}
                  {aiRawData.gender && (
                    <div>
                      <span className="text-light-text-tertiary dark:text-dark-text-tertiary">Gender: </span>
                      <span className="text-light-text-primary dark:text-dark-text-primary font-medium">{aiRawData.gender}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Contacts */}
              {aiRawData.contacts && aiRawData.contacts.length > 0 && (
                <div className={`rounded-lg p-4 border ${unmappedContactsCount > 0
                  ? 'bg-amber-50/50 dark:bg-amber-950/10 border-amber-300 dark:border-amber-700/50'
                  : 'bg-light-bg-secondary dark:bg-dark-bg-primary border-light-border-primary dark:border-dark-border-primary'
                }`}>
                  <SectionHeader
                    title="Contacts"
                    totalCount={aiRawData.contacts.length}
                    unmappedCount={unmappedContactsCount}
                  />
                  <div className="space-y-1.5 text-sm">
                    {/* Mapped contacts */}
                    {mappedContacts.map((contact, idx) => (
                      <div key={`m-${idx}`} className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-green-500 dark:text-green-400 flex-shrink-0" />
                        <span className="text-light-text-tertiary dark:text-dark-text-tertiary capitalize">{contact.type}:</span>
                        <span className="text-light-text-primary dark:text-dark-text-primary font-mono">{contact.value}</span>
                      </div>
                    ))}
                    {/* Unmapped contacts */}
                    {unmapped?.contacts?.map((contact, idx) => (
                      <div key={`u-${idx}`} className="flex items-center gap-2">
                        <AlertCircle className="w-3 h-3 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                        <span className="text-light-text-tertiary dark:text-dark-text-tertiary capitalize">{contact.type}:</span>
                        <span className="text-light-text-primary dark:text-dark-text-primary font-mono">{contact.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Professions */}
              {aiRawData.professions && aiRawData.professions.length > 0 && (
                <div className={`rounded-lg p-4 border ${unmappedProfessionsCount > 0
                  ? 'bg-amber-50/50 dark:bg-amber-950/10 border-amber-300 dark:border-amber-700/50'
                  : 'bg-light-bg-secondary dark:bg-dark-bg-primary border-light-border-primary dark:border-dark-border-primary'
                }`}>
                  <SectionHeader
                    title="Professions"
                    totalCount={aiRawData.professions.length}
                    unmappedCount={unmappedProfessionsCount}
                  />
                  <div className="flex flex-wrap gap-2">
                    {mappedProfessions.map((prof, idx) => (
                      <span
                        key={`m-${idx}`}
                        className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                      >
                        {prof}
                      </span>
                    ))}
                    {unmapped?.professions?.map((prof, idx) => (
                      <span
                        key={`u-${idx}`}
                        className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-300 dark:border-amber-700"
                      >
                        {prof}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Tools */}
              {aiRawData.tools && aiRawData.tools.length > 0 && (
                <div className={`rounded-lg p-4 border ${unmappedToolsCount > 0
                  ? 'bg-amber-50/50 dark:bg-amber-950/10 border-amber-300 dark:border-amber-700/50'
                  : 'bg-light-bg-secondary dark:bg-dark-bg-primary border-light-border-primary dark:border-dark-border-primary'
                }`}>
                  <SectionHeader
                    title="Tools"
                    totalCount={aiRawData.tools.length}
                    unmappedCount={unmappedToolsCount}
                  />
                  <div className="flex flex-wrap gap-2">
                    {mappedTools.map((tool, idx) => (
                      <span
                        key={`m-${idx}`}
                        className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                  {/* Unmapped tools — separate list with Create buttons */}
                  {unmappedToolsCount > 0 && (
                    <div className="mt-3 pt-3 border-t border-amber-300 dark:border-amber-700/50 space-y-2">
                      {unmapped.tools.map((tool, idx) => (
                        <div
                          key={`u-${idx}`}
                          className="flex items-center justify-between gap-3 bg-white dark:bg-dark-bg-primary rounded-lg p-2.5 border border-amber-300 dark:border-amber-700/50"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <AlertCircle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                            <span className="text-sm text-light-text-primary dark:text-dark-text-primary font-medium truncate">
                              {tool}
                            </span>
                          </div>
                          {allowCreateTool && (
                            <button
                              onClick={() => handleCreateTool(tool)}
                              disabled={creatingTools.has(tool)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-lg transition-all disabled:cursor-not-allowed shadow-sm hover:shadow whitespace-nowrap"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              {creatingTools.has(tool) ? 'Creating...' : 'Create'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Languages */}
              {aiRawData.languages && aiRawData.languages.length > 0 && (
                <div className={`rounded-lg p-4 border ${unmappedLanguagesCount > 0
                  ? 'bg-amber-50/50 dark:bg-amber-950/10 border-amber-300 dark:border-amber-700/50'
                  : 'bg-light-bg-secondary dark:bg-dark-bg-primary border-light-border-primary dark:border-dark-border-primary'
                }`}>
                  <SectionHeader
                    title="Languages"
                    totalCount={aiRawData.languages.length}
                    unmappedCount={unmappedLanguagesCount}
                  />
                  <div className="space-y-1.5 text-sm">
                    {/* Mapped languages */}
                    {mappedLanguages.map((lang, idx) => (
                      <div key={`m-${idx}`} className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-green-500 dark:text-green-400 flex-shrink-0" />
                        <span className="text-light-text-primary dark:text-dark-text-primary font-medium">{lang.language}</span>
                        {lang.level && (
                          <>
                            <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>
                            <span className="text-light-text-secondary dark:text-dark-text-secondary">{lang.level}</span>
                          </>
                        )}
                      </div>
                    ))}
                    {/* Unmapped languages */}
                    {unmapped?.languages?.map((lang, idx) => (
                      <div key={`u-${idx}`} className="flex items-center gap-2">
                        <AlertCircle className="w-3 h-3 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                        <span className="text-light-text-primary dark:text-dark-text-primary font-medium">{lang.language}</span>
                        {lang.level && (
                          <>
                            <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>
                            <span className="text-light-text-secondary dark:text-dark-text-secondary">{lang.level}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              {aiRawData.summary && (
                <div className="bg-light-bg-secondary dark:bg-dark-bg-primary rounded-lg p-4 border border-light-border-primary dark:border-dark-border-primary">
                  <SectionHeader title="Summary" totalCount={0} unmappedCount={0} alwaysGreen />
                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary whitespace-pre-wrap">
                    {aiRawData.summary}
                  </p>
                </div>
              )}

              {/* Work Experience */}
              {aiRawData.work_experience && aiRawData.work_experience.length > 0 && (
                <div className="bg-light-bg-secondary dark:bg-dark-bg-primary rounded-lg p-4 border border-light-border-primary dark:border-dark-border-primary">
                  <h3 className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-green-600 dark:text-green-400" />
                    Work Experience ({aiRawData.work_experience.length})
                  </h3>
                  <div className="space-y-3">
                    {aiRawData.work_experience.map((exp, idx) => (
                      <div key={idx} className="text-sm border-l-2 border-light-border-secondary dark:border-dark-border-secondary pl-3">
                        <div className="font-medium text-light-text-primary dark:text-dark-text-primary">
                          {exp.position}{exp.company && ` — ${exp.company}`}
                        </div>
                        {(exp.start_date || exp.end_date) && (
                          <div className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-0.5">
                            {exp.start_date || '?'} — {exp.end_date || 'Present'}
                          </div>
                        )}
                        {exp.description && (
                          <>
                            <p className={`text-light-text-secondary dark:text-dark-text-secondary mt-1 ${expandedDescriptions.has(idx) ? '' : 'line-clamp-2'}`}>
                              {exp.description}
                            </p>
                            <button
                              onClick={() => setExpandedDescriptions(prev => {
                                const next = new Set(prev);
                                next.has(idx) ? next.delete(idx) : next.add(idx);
                                return next;
                              })}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-0.5"
                            >
                              {expandedDescriptions.has(idx) ? 'Show less' : 'Show more'}
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Education */}
              {aiRawData.education && aiRawData.education.length > 0 && (
                <div className="bg-light-bg-secondary dark:bg-dark-bg-primary rounded-lg p-4 border border-light-border-primary dark:border-dark-border-primary">
                  <h3 className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary mb-3 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-green-600 dark:text-green-400" />
                    Education ({aiRawData.education.length})
                  </h3>
                  <div className="space-y-3">
                    {aiRawData.education.map((edu, idx) => (
                      <div key={idx} className="text-sm border-l-2 border-light-border-secondary dark:border-dark-border-secondary pl-3">
                        <div className="font-medium text-light-text-primary dark:text-dark-text-primary">
                          {edu.degree}{edu.institution && ` — ${edu.institution}`}
                        </div>
                        {(edu.start_date || edu.end_date) && (
                          <div className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-0.5">
                            {edu.start_date || '?'} — {edu.end_date || 'Present'}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {aiRawData.certifications && aiRawData.certifications.length > 0 && (
                <div className="bg-light-bg-secondary dark:bg-dark-bg-primary rounded-lg p-4 border border-light-border-primary dark:border-dark-border-primary">
                  <h3 className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary mb-3 flex items-center gap-2">
                    <Award className="w-4 h-4 text-green-600 dark:text-green-400" />
                    Certifications ({aiRawData.certifications.length})
                  </h3>
                  <div className="space-y-2">
                    {aiRawData.certifications.map((cert, idx) => (
                      <div key={idx} className="text-sm border-l-2 border-light-border-secondary dark:border-dark-border-secondary pl-3">
                        <div className="font-medium text-light-text-primary dark:text-dark-text-primary">
                          {cert.name}{cert.provider && ` — ${cert.provider}`}
                        </div>
                        {cert.date && (
                          <div className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-0.5">
                            {cert.date}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expected Rate */}
              {aiRawData.expected_rate && (
                <div className="bg-light-bg-secondary dark:bg-dark-bg-primary rounded-lg p-4 border border-light-border-primary dark:border-dark-border-primary">
                  <h3 className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                    Expected Rate
                  </h3>
                  <p className="text-sm text-light-text-primary dark:text-dark-text-primary font-medium">
                    {aiRawData.expected_rate}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ParsedDataPanel;
